import { uniq } from 'es-toolkit';

import { CalculationError, CreateTrussError, TrussEnvelope } from '@paragon/contract-library';
import { assertExhaustiveSwitch, formatAsNaturalLanguageList, naturalSort } from '@paragon/utilities';

export function shouldIgnoreServerGeneratedError(error: CreateTrussError): boolean {
  // Truss solid generation errors should only be reported from the client. This could cause confusion in the uncommon
  // (and essentially buggy) cases where client truss solid generation produces a successful result and the server does
  // not, but it prevents confusion when the layout has been edited since the server error was reported.
  return error.type === 'TrussSolid';
}

export interface FormattedError {
  readonly affectedEnvelopeNames: readonly string[];
  readonly message: string;
  readonly documentationUrl?: string;
}

export function formatDesignEnvelopeError(envelope: TrussEnvelope, error: CreateTrussError): FormattedError {
  switch (error.type) {
    case 'TrussSolid':
      return formatCalculationError(envelope, error.error);
    case 'MemberGeneration':
      return {
        affectedEnvelopeNames: [envelope.name],
        message: `${prelude(envelope)} Could not produce a ${error.isProfileTruss ? 'profile truss' : 'feature pattern truss'} from the profile.`,
      };
    case 'MissingJacks':
      return formatMissingJacksError(envelope, error);
    case 'FeaturePatternInfo':
      return formatFeaturePatternInfoError(envelope, error);
    case 'Unexpected':
      const errorMessage =
        error.userVisibleExceptionMessage ?? 'There was an error. Please contact Paragon Customer Support for help.';
      return { affectedEnvelopeNames: [envelope.name], message: `${prelude(envelope)} ${errorMessage}` };
    default:
      assertExhaustiveSwitch(error);
  }
}

function prelude({ name }: TrussEnvelope) {
  return `${name}:`;
}

function formatCalculationError(envelope: TrussEnvelope, error: CalculationError) {
  const boundarySide =
    error.boundarySide === 'Top'
      ? 'top'
      : error.boundarySide === 'Bottom'
        ? 'bottom'
        : error.boundarySide == null
          ? 'top/bottom'
          : assertExhaustiveSwitch(error.boundarySide);
  const boundaryType =
    error.boundaryType === 'OrderedCuts'
      ? 'ordered cuts on the truss envelope'
      : error.boundaryType == null
        ? 'colliding objects'
        : error.boundaryType === 'Inferred'
          ? envelope.roofContainerGuid != null
            ? 'roof container'
            : envelope.featurePatternContainerGuid != null
              ? 'feature pattern container'
              : 'roof planes, ceiling planes, and trays'
          : assertExhaustiveSwitch(error.boundaryType);
  const trussSide = (item: 'side' | 'corner') =>
    error.trussSide === 'Left'
      ? `left ${item}`
      : error.trussSide === 'Right'
        ? `right ${item}`
        : error.trussSide === 'LeftAndRight'
          ? `left and right ${item}s`
          : error.trussSide == null
            ? `a ${item}`
            : assertExhaustiveSwitch(error.trussSide);
  const errorWithoutEnvelopeNames: Omit<FormattedError, 'affectedEnvelopeNames'> = (() => {
    switch (error.type) {
      case 'NullContainerSolid':
        return { message: `${prelude(envelope)} The ${boundaryType} has no shape.` };
      case 'ZedgeEnvelope':
        return { message: `${prelude(envelope)} The left and right points cannot be the same.` };
      case 'VerticalTerminalOrderedCuts':
        return {
          message: `${prelude(envelope)} The first or last ordered cut on the ${boundarySide} is vertical. Did you forget to add a horizontal cut?`,
        };
      case 'OrderedCutsParallelToFace':
        return { message: `${prelude(envelope)} An ordered cut on the ${boundarySide} is parallel to the truss face.` };
      case 'ConsecutiveParallelOrderedCuts':
        return { message: `${prelude(envelope)} Disallowed consecutive parallel ordered cuts on the ${boundarySide}.` };
      case 'NullCutFacet':
        return { message: `${prelude(envelope)} An ordered cut's shape could not be computed on the ${boundarySide}.` };
      case 'DisorientedOrderedCuts':
        return {
          message: `${prelude(envelope)} Top boundary lies below the bottom boundary on the ${trussSide('side')}.`,
        };
      case 'BoundaryCornerFailure':
        return {
          message: `${prelude(envelope)} Boundary could not be closed on the ${boundarySide} ${trussSide('corner')}. Check the ${boundaryType}.`,
        };
      case 'BoundaryFailure':
        return {
          message: `${prelude(envelope)} No closed boundary on the ${boundarySide}. Check the ${boundaryType}.`,
        };
      case 'NullTrussPolygon':
        return { message: `${prelude(envelope)} Top and bottom boundaries intersect each other.` };
      case 'MissingDependencies':
        return { message: `${prelude(envelope)} Some referenced objects could not be found.` };
      case 'CalculationPanic':
        return { message: `${prelude(envelope)} Unexpected error encountered.` };
      default:
        assertExhaustiveSwitch(error.type);
    }
  })();
  return { ...errorWithoutEnvelopeNames, affectedEnvelopeNames: [envelope.name] };
}

function formatMissingJacksError(
  envelope: TrussEnvelope,
  error: CreateTrussError & { type: 'MissingJacks' },
): FormattedError {
  const jackNamesText = formatAsNaturalLanguageList(error.missingJackEnvelopeNames.toSorted(naturalSort), 'and');
  const jacksHave =
    error.missingJackEnvelopeNames.length === 1 ? `Jack ${jackNamesText} has` : `Jacks ${jackNamesText} have`;
  return {
    affectedEnvelopeNames: [envelope.name],
    message: `${prelude(envelope)} ${jacksHave} not been designed. A girder's jacks must be designed first.`,
  };
}

function formatFeaturePatternInfoError(
  envelope: TrussEnvelope,
  { error }: CreateTrussError & { type: 'FeaturePatternInfo' },
): FormattedError {
  const message = (() => {
    switch (error) {
      case 'NoBearings':
        return `${prelude(envelope)} Could not find any bearings`;
      case 'ProfileUnsupported':
        return `${prelude(envelope)} Could not determine the profile of this feature pattern truss`;
      case 'SlopeAndStepsUnsupported':
        return `${prelude(envelope)} Cannot design a feature pattern truss with a sloped bottom chord and steps`;
      default:
        assertExhaustiveSwitch(error);
    }
  })();
  return { affectedEnvelopeNames: [envelope.name], message };
}

export function aggregateErrors(errors: readonly FormattedError[]): FormattedError | null {
  if (errors.length < 2) return errors[0] ?? null;

  const affectedEnvelopeNames = uniq(errors.flatMap(({ affectedEnvelopeNames }) => affectedEnvelopeNames)).toSorted(
    naturalSort,
  );

  const erroredEnvelopeNames = formatAsNaturalLanguageList(affectedEnvelopeNames, 'and');
  return { affectedEnvelopeNames, message: `Envelopes ${erroredEnvelopeNames} have errors.` };
}
